import React, { useState } from "react";

import { Loader2, RotateCcw } from "lucide-react";

import { errorText, formatDateTime } from "./format";
import {
  type SignalAdminChange,
  type SignalAdminVersion,
  valueLabel,
} from "./types";
import Button from "../../Components/Button";
import CommonModal from "../../Components/Modal";
import { useAppDispatch } from "../../Store/Hooks";
import {
  previewSignalAdminRestore,
  restoreSignalAdminVersion,
} from "../../Store/Reducers/SignalAdminSlice";

interface ChangeAuditPanelProps {
  versions: SignalAdminVersion[];
  loading: boolean;
  /** The version in effect now (can't be restored onto itself). */
  currentVersion: number;
  /** Restoring replaces the whole config, so unapplied edits must go first. */
  hasUnappliedChanges: boolean;
  onRestored: (
    version: number,
    reevaluation?: { evaluate?: boolean; public_data?: boolean }
  ) => void;
}

const ChangeList: React.FC<{ changes: SignalAdminChange[] }> = ({
  changes,
}) => (
  <ul className="flex flex-col gap-1">
    {changes.map(c => (
      <li key={c.field}>
        <span className="text-gray-500">{c.label}:</span>{" "}
        <span className="text-gray-500 line-through decoration-gray-300">
          {valueLabel(c.from)}
        </span>{" "}
        →{" "}
        <span className="font-semibold text-gray-900">{valueLabel(c.to)}</span>
      </li>
    ))}
  </ul>
);

const RestoreModal: React.FC<{
  version: number;
  onClose: () => void;
  onRestored: ChangeAuditPanelProps["onRestored"];
}> = ({ version, onClose, onRestored }) => {
  const dispatch = useAppDispatch();
  const [changes, setChanges] = useState<SignalAdminChange[] | null>(null);
  const [error, setError] = useState("");
  const [reason, setReason] = useState("");
  const [restoring, setRestoring] = useState(false);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const diff = await dispatch(
          previewSignalAdminRestore(version)
        ).unwrap();
        if (!cancelled) setChanges(diff ?? []);
      } catch (e) {
        if (!cancelled)
          setError(errorText(e, "Couldn't compare that version."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, version]);

  const restore = async () => {
    setRestoring(true);
    setError("");
    try {
      const res = await dispatch(
        restoreSignalAdminVersion({ version, reason: reason.trim() })
      ).unwrap();
      onClose();
      onRestored(res?.applied?.version ?? version, res?.reevaluation);
    } catch (e) {
      setError(errorText(e, "Couldn't restore that version."));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <CommonModal
      isOpen
      toggle={() => !restoring && onClose()}
      title={`Restore version ${version}`}
      subtitle={
        <span className="text-[13px] font-normal text-gray-500">
          All organizations · saved as a new version; nothing is overwritten
        </span>
      }
      footer={
        <div className="flex w-full items-center justify-end gap-2">
          <Button variant="white" onClick={onClose} disabled={restoring}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => void restore()}
            disabled={!reason.trim() || !changes?.length}
            loading={restoring}
          >
            Restore
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4 text-[12.5px]">
        {changes === null && !error && (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Comparing with what&apos;s live now…
          </div>
        )}
        {changes && changes.length === 0 && (
          <p className="text-gray-600">
            Version {version} matches what&apos;s live now — nothing to restore.
          </p>
        )}
        {changes && changes.length > 0 && (
          <div className="max-h-[300px] overflow-y-auto rounded-lg border border-gray-200 px-3 py-2">
            <ChangeList changes={changes} />
          </div>
        )}
        <label className="flex flex-col gap-1.5">
          <span className="text-xs font-semibold text-gray-700">
            Reason <span className="text-red-600">*</span>
          </span>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            rows={3}
            placeholder="Why go back to this version? Recorded in Change Audit."
            className="w-full resize-y rounded-lg border border-gray-300 px-3 py-2 text-[13px] text-gray-900 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
          />
        </label>
        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700">
            {error}
          </div>
        )}
      </div>
    </CommonModal>
  );
};

const ChangeAuditPanel: React.FC<ChangeAuditPanelProps> = ({
  versions,
  loading,
  currentVersion,
  hasUnappliedChanges,
  onRestored,
}) => {
  const [restoring, setRestoring] = useState<number | null>(null);
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="border-b border-gray-100 px-5 py-3">
        <h3 className="text-[13px] font-bold text-gray-900">Change Audit</h3>
        <p className="mt-0.5 text-xs text-gray-500">
          Every applied platform change, newest first. Entries can&apos;t be
          edited or removed; restoring an earlier version adds a new one.
        </p>
      </div>
      {loading && versions.length === 0 ? (
        <p className="px-5 py-8 text-center text-[12.5px] text-gray-400">
          Loading…
        </p>
      ) : versions.length === 0 ? (
        <p className="px-5 py-8 text-center text-[12.5px] text-gray-500">
          No platform changes have been applied yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] border-collapse text-left">
            <thead>
              <tr className="bg-gray-50 text-[10.5px] font-bold uppercase tracking-[0.07em] text-gray-400">
                <th className="px-5 py-2.5">Version</th>
                <th className="px-3 py-2.5">Applied</th>
                <th className="px-3 py-2.5">Change</th>
                <th className="px-3 py-2.5">Reason</th>
                <th className="px-3 py-2.5">Admin</th>
                <th className="px-5 py-2.5" aria-label="Restore" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 text-[12.5px] text-gray-700">
              {versions.map(v => (
                <tr key={v.version} className="align-top">
                  <td className="px-5 py-3">
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-gray-600">
                      v{v.version}
                    </span>
                    {v.version === currentVersion && (
                      <div className="mt-1 text-[10.5px] font-semibold text-emerald-700">
                        Live
                      </div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-gray-500">
                    {formatDateTime(v.applied_at)}
                  </td>
                  <td className="px-3 py-3">
                    <ChangeList changes={v.changes} />
                  </td>
                  <td className="max-w-[320px] px-3 py-3 text-gray-600">
                    {v.reason}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-gray-500">
                    {v.applied_by_email ?? "Izba admin"}
                  </td>
                  <td className="px-5 py-3">
                    {v.version !== currentVersion && (
                      <button
                        type="button"
                        disabled={hasUnappliedChanges}
                        title={
                          hasUnappliedChanges
                            ? "Apply or discard your unapplied changes first."
                            : `Restore the settings as they were in v${v.version}`
                        }
                        onClick={() => setRestoring(v.version)}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-gray-200 px-2.5 py-1 text-[12px] font-semibold text-gray-600 hover:border-brand hover:text-brand disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                        Restore
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {restoring !== null && (
        <RestoreModal
          version={restoring}
          onClose={() => setRestoring(null)}
          onRestored={onRestored}
        />
      )}
    </section>
  );
};

export default ChangeAuditPanel;
